import React from 'react';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid';
import { cx } from './cx';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** 'none' for a list whose rows bring their own padding. */
  padding?: 'none' | 'md' | 'lg';
}

/** A white surface with a hairline border, for a list, a household or a form section. */
export function Card({ padding = 'md', className, ...rest }: CardProps) {
  return (
    <div
      className={cx(
        'rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900 dark:shadow-none',
        padding === 'md' && 'p-4',
        padding === 'lg' && 'p-6',
        className,
      )}
      {...rest}
    />
  );
}

export interface EmptyStateProps {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Usually one primary Button: the next thing to do. */
  action?: React.ReactNode;
  className?: string;
}

/** What a page shows when there's nothing in it yet, with the step that fills it. */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cx('flex flex-col items-center px-6 py-12 text-center', className)}>
      {Icon && (
        <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
      )}
      <h3 className="text-base font-semibold text-gray-900 text-balance dark:text-gray-100">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

/** A grey placeholder block that pulses while content loads. */
export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-md bg-gray-200 dark:bg-gray-800 motion-reduce:animate-none', className)} aria-hidden="true" />;
}

/** Placeholder rows shaped like a list of chores, announced once as "Loading…". */
export function SkeletonList({ rows = 4, label = 'Loading…' }: { rows?: number; label?: string }) {
  return (
    <Card padding="none" role="status" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3 border-gray-100 dark:border-gray-800 px-4 py-3 [&:not(:first-child)]:border-t">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-1/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
          <Skeleton className="h-5 w-16" />
        </div>
      ))}
    </Card>
  );
}

const ALERTS = {
  info: { box: 'border-indigo-200 bg-indigo-50 text-indigo-900 dark:border-indigo-400/30 dark:bg-indigo-500/15 dark:text-indigo-100', icon: InformationCircleIcon, iconColour: 'text-indigo-500 dark:text-indigo-400' },
  success: { box: 'border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-400/30 dark:bg-emerald-500/15 dark:text-emerald-100', icon: CheckCircleIcon, iconColour: 'text-emerald-500 dark:text-emerald-400' },
  warning: { box: 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/15 dark:text-amber-100', icon: ExclamationTriangleIcon, iconColour: 'text-amber-500 dark:text-amber-400' },
  error: { box: 'border-red-200 bg-red-50 text-red-900 dark:border-red-400/30 dark:bg-red-500/15 dark:text-red-100', icon: XCircleIcon, iconColour: 'text-red-500 dark:text-red-400' },
};

export interface AlertProps {
  tone: keyof typeof ALERTS;
  title?: React.ReactNode;
  children?: React.ReactNode;
  /** A button or link at the right, e.g. "Dismiss". */
  action?: React.ReactNode;
  className?: string;
}

/** A boxed message in the flow of the page: an error, a warning, or a confirmation. */
export function Alert({ tone, title, children, action, className }: AlertProps) {
  const { box, icon: Icon, iconColour } = ALERTS[tone];
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={cx('flex items-start gap-3 rounded-lg border p-3 text-sm', box, className)}>
      <Icon className={cx('mt-0.5 h-5 w-5 flex-none', iconColour)} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        {title && <p className="font-medium">{title}</p>}
        {children && <div className={cx(!!title && 'mt-0.5', 'opacity-90')}>{children}</div>}
      </div>
      {action && <div className="flex-none">{action}</div>}
    </div>
  );
}
